import type { DragOverEvent } from '@dnd-kit/react';
import type { DataGridColumnProps } from '../DataGrid';

import { DragDropProvider, useDroppable } from '@dnd-kit/react';
import { isSortable, useSortable } from '@dnd-kit/react/sortable';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { move } from '@dnd-kit/helpers';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../../lib/helpers';

import {
  DraggableRectanglesIcon,
  GearIcon,
  MinusIcon,
  PlusIcon,
} from '../../../images/shapes';

import Popover from '../../Popover/Popover';

interface ColumnSettingsPopoverProps {
  columns: DataGridColumnProps[];
  onChange: (columns: DataGridColumnProps[]) => void;
  testId?: string;
}

type ColumnGroup = 'active' | 'available';

const sortableTransition = {
  duration: 300,
  easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
  idle: true,
};

const ColumnSettingsPopover = ({
  columns,
  onChange,
  testId,
}: ColumnSettingsPopoverProps) => {
  const { t } = useTranslation();
  const originalColumns = useRef(columns);
  const configurableColumns = columns.filter(
    (column) => column.removable !== false,
  );

  const visibleColumns = configurableColumns.filter((column) => !column.hide);
  const hiddenColumns = configurableColumns.filter((column) => column.hide);

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { source, target } = event.operation;
      if (
        !isSortable(source) ||
        !isSortable(target) ||
        source.sortable.group !== target.sortable.group
      ) {
        // React handles cross-list moves; avoid dnd-kit's DOM reparenting there.
        event.preventDefault();
      }

      const groups = {
        active: configurableColumns
          .filter((column) => !column.hide)
          .map((column) => column.accessor),
        available: configurableColumns
          .filter((column) => column.hide)
          .map((column) => column.accessor),
      };
      const moved = move(groups, event);
      if (moved === groups) return;

      const columnById = new Map(
        columns.map((column) => [column.accessor, column]),
      );
      const visible = new Set(moved.active);
      const reordered = [...moved.active, ...moved.available].map(
        (accessor) => ({
          ...columnById.get(accessor)!,
          hide: !visible.has(accessor),
        }),
      );

      // Keep technical, non-configurable columns in their original positions.
      let index = 0;
      onChange(
        columns.map((column) =>
          column.removable === false ? column : reordered[index++],
        ),
      );
    },
    [columns, configurableColumns, onChange],
  );

  const toggleColumn = (column: DataGridColumnProps) => {
    onChange(
      columns.map((item) =>
        item.accessor === column.accessor
          ? { ...item, hide: !item.hide }
          : item,
      ),
    );
  };

  return (
    <Popover
      popoverButtonProps={{ 'aria-label': t('Global.Columns') }}
      popoverButton={
        <div
          className={twMerge(
            'flex size-6 items-center justify-center rounded',
            'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700',
          )}
        >
          <GearIcon className="size-3" />
        </div>
      }
      popoverPanelAnchorTo="bottom end"
      additionalClasses="m-auto"
      additionalPopoverPanelClasses="border shadow-none scrollbar-sm dark:bg-slate-800"
      content={
        <DragDropProvider
          onDragStart={() => {
            originalColumns.current = columns;
          }}
          onDragOver={handleDragOver}
          onDragEnd={(event) => {
            if (event.canceled) onChange(originalColumns.current);
          }}
        >
          <h3 className="px-3 py-2 text-base font-normal text-slate-500 dark:text-slate-400">
            {t('ObjectsOfType.ShowInTable')}
          </h3>
          <ColumnSettingsGroup
            columns={visibleColumns}
            group="active"
            onToggle={toggleColumn}
            testId={testId}
          />
          {hiddenColumns.length > 0 && (
            <>
              <hr className="border-slate-200 dark:border-slate-700" />
              <ColumnSettingsGroup
                columns={hiddenColumns}
                group="available"
                onToggle={toggleColumn}
                testId={testId}
              />
            </>
          )}
        </DragDropProvider>
      }
      {...getTestProps(testId, 'column-settings', 'testId')}
    />
  );
};

const ColumnSettingsGroup = ({
  columns,
  group,
  onToggle,
  testId,
}: {
  columns: DataGridColumnProps[];
  group: ColumnGroup;
  onToggle: (column: DataGridColumnProps) => void;
  testId?: string;
}) => {
  const { t } = useTranslation();
  const hidden = group === 'available';
  const { ref } = useDroppable({
    id: group,
    // Non-empty groups should receive drops on their sortable rows, not the list.
    disabled: columns.length > 0,
  });

  return (
    <ul
      ref={ref}
      aria-label={t(
        hidden ? 'ObjectsOfType.HiddenColumns' : 'ObjectsOfType.ShowInTable',
      )}
      {...getTestProps(
        testId,
        hidden ? 'column-settings-hidden' : 'column-settings-visible',
      )}
    >
      {columns.map((column, index) => (
        <ColumnSettingsItem
          key={column.accessor}
          column={column}
          index={index}
          group={group}
          onToggle={() => onToggle(column)}
          testId={testId}
        />
      ))}
    </ul>
  );
};

const ColumnSettingsItem = ({
  column,
  index,
  group,
  onToggle,
  testId,
}: {
  column: DataGridColumnProps;
  index: number;
  group: ColumnGroup;
  onToggle: () => void;
  testId?: string;
}) => {
  const { t } = useTranslation();
  const hidden = group === 'available';
  const label =
    typeof column.label === 'string' ? column.label : column.accessor;

  const { isDragging, ref, handleRef } = useSortable({
    id: column.accessor,
    index,
    group,
    modifiers: [RestrictToVerticalAxis],
    transition: sortableTransition,
  });

  const ActionIcon = hidden ? PlusIcon : MinusIcon;

  return (
    <li
      ref={(element) => {
        ref(element);
        handleRef(element);
      }}
      onPointerDownCapture={(event) => {
        if ((event.target as HTMLElement).closest('[data-no-drag]')) {
          event.stopPropagation();
        }
      }}
      onKeyDownCapture={(event) => {
        if ((event.target as HTMLElement).closest('[data-no-drag]')) {
          event.stopPropagation();
        }
      }}
      className={twMerge(
        'group flex cursor-grab items-center gap-2 px-3 py-2 active:cursor-grabbing',
        isDragging && 'opacity-50 contain-[layout_paint]',
      )}
      {...getTestProps(testId, `column-settings-item-${column.accessor}`)}
    >
      <button
        type="button"
        className="flex shrink-0 cursor-grab items-center justify-center active:cursor-grabbing"
        aria-label={t('ObjectsOfType.MoveColumn', { column: label })}
      >
        <DraggableRectanglesIcon className="h-3 w-auto text-slate-400" />
      </button>
      <span
        className={twMerge(
          'min-w-0 flex-1 truncate text-base font-normal',
          hidden
            ? 'text-slate-500 dark:text-slate-400'
            : 'text-slate-900 dark:text-slate-200',
        )}
      >
        {column.label}
      </span>
      <button
        type="button"
        className={twMerge(
          'flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded',
          'hover:bg-slate-100 focus-visible:bg-slate-100 dark:hover:bg-slate-700',
          !hidden &&
            'sm:opacity-0 group-hover:opacity-100 group-focus-within:opacity-100',
        )}
        aria-label={t(
          hidden ? 'ObjectsOfType.ShowColumn' : 'ObjectsOfType.HideColumn',
          { column: label },
        )}
        onClick={onToggle}
        data-no-drag
        {...getTestProps(
          testId,
          `column-settings-item-${column.accessor}-toggle`,
        )}
      >
        <ActionIcon className="h-2 w-2 text-slate-900 dark:text-slate-200" />
      </button>
    </li>
  );
};

export default ColumnSettingsPopover;
